<!--
Address autocomplete component based on PrimeVue AutoComplete and an address resolution service

Format of the object returned in the v-model:
{
  longitude: Number,
  latitude: Number,
  name: String,
  postcode: String,
  city: String
}
-->
<template>
  <div class="p-0">
    <AutoComplete
      v-model="v$.address.$model"
      :option-label="formatAddress"
      :suggestions="items"
      @item-select="handleItemSelect"
      @complete="search"
      @blur="handleBlur"
    />
    <div
      v-for="error of v$.address.$errors"
      :key="error.$uid"
      class="input-errors"
    >
      <span class="error-msg">{{ error.$message }}</span>
    </div>
  </div>
</template>

<script>
import AutoComplete from 'primevue/autocomplete';
import {minLength} from "@/i18n/i18nValidators";
import {useVuelidate} from "@vuelidate/core";

export default {
  name: "InputAddress",
  components: {AutoComplete},
  props: {
    modelValue: {
      type: Object,
      default: null
    }
  },
  emits: ['update:modelValue'],
  setup() {
    return {
      v$: useVuelidate()
    }
  },
  data() {
    return {
      address: this.modelValue,
      timeout: null,
      items: [],
    };
  },
  validations() {
    return {
      // The address resolution API expects at least 3 characters.
      address: {minLength: minLength(3)},
    }
  },
  watch: {
    async address() {
      await this.v$.$validate();
    },
    modelValue: {
      immediate: true,
      handler(value) {
        this.address = value;
      }
    }
  },
  beforeUnmount() {
    clearTimeout(this.timeout);
  },
  methods: {
    /**
     * Formats addresses in the format: street name, postcode, town
     * Example : 73 Avenue de Paris, Saint-Mandé
     */
    formatAddress(address) {
      if (!address) return '';
      return `${address.name}, ${address.postcode} ${address.city}`;
    },

    /**
     * Handles searches based on the address typed
     */
    async search(event) {
      // Clears the timeout of the previous request
      clearTimeout(this.timeout);
      // Défined a timeout between requests to limit API calls
      this.timeout = setTimeout(async () => {
        const query = event.query.trim();

        if (query.length >= 3) {
          try {
            const {features} = await this.$addressService.searchByAddress(query);

            this.items = features.map(feature => ({
              longitude: feature.geometry.coordinates[0],
              latitude: feature.geometry.coordinates[1],
              name: feature.properties.name,
              postcode: feature.properties.postcode,
              city: feature.properties.city,
            }));
          } catch (e) {
            this.items = []
            console.error(e);
          }
        }
      }, 250);
    },

    /**
     * Handles item selection in the results list
     */
    handleItemSelect(event) {
      this.$emit('update:modelValue', event.value)
    },

    /**
     * Handles loss of focus on input
     */
    handleBlur() {
      // Clears the model value if the address is incomplete
      if (!this.address || typeof this.address !== 'object') {
        this.$emit('update:modelValue', null);
      }
      if (!this.modelValue) {
        this.address = null;
      }
    }
  }
}
</script>

<style>
.p-autocomplete, .p-autocomplete-input {
  width: 100% !important;
}
</style>
